// Reusable right-side panel that overlays the main content area.
// Renders via portal into the layout's panel container (absolute positioned,
// below the header, within the SidebarInset mega card).
//
// Sizes:
//   md  — less than half the content area (~45%)
//   lg  — more than half (~70%)
//   xl  — nearly full width (just margins)

import * as React from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

export type PanelSize = 'md' | 'lg' | 'xl'

interface DetailPanelProps {
  open: boolean
  onClose: () => void
  size?: PanelSize
  children: React.ReactNode
  className?: string
}

const sizeStyles: Record<PanelSize, string> = {
  md: 'left-[55%]',
  lg: 'left-[30%]',
  xl: 'left-3',
}

export function DetailPanel({
  open,
  onClose,
  size = 'lg',
  children,
  className,
}: DetailPanelProps) {
  const container = React.useContext(PanelContainerContext)

  if (!open || !container) return null

  return createPortal(
    <div className="absolute inset-0 pointer-events-auto z-40 bg-background/80 backdrop-blur-sm">
      {/* Backdrop */}
      <div data-clickable="true" className="absolute inset-0" onClick={onClose} />
      {/* Panel */}
      <div
        className={cn(
          'absolute top-3 right-3 bottom-3 rounded-xl bg-card text-card-foreground border shadow-2xl',
          'flex flex-col overflow-hidden',
          'animate-in slide-in-from-right-4 duration-200',
          sizeStyles[size],
          className,
        )}
      >
        {children}
      </div>
    </div>,
    container,
  )
}

// ── Sub-components for consistent panel layout ──

export function DetailPanelHeader({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('px-6 pt-5 pb-4 border-b shrink-0', className)}
      {...props}
    >
      {children}
    </div>
  )
}

export function DetailPanelFooter({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'px-6 py-4 border-t bg-card shrink-0 flex items-center gap-2',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

// ── Context for the portal container ──

export const PanelContainerContext = React.createContext<HTMLDivElement | null>(null)
